import { useContext, useEffect, useState } from "react";
import {
  FaBookOpen,
  FaBullhorn,
  FaComments,
  FaHome,
  FaInbox,
  FaMagic,
  FaProjectDiagram,
  FaSignOutAlt,
  FaUser,
  FaUserFriends,
  FaUsers,
} from "react-icons/fa";
import { NavLink, useNavigate } from "react-router-dom";
import { getIncomingConnectionRequests } from "../api/connections";
import { getMyProfile } from "../api/profile";
import { AuthContext } from "../context/AuthContext";
import { onWsConnect, safeSubscribe } from "../realtime/wsClient";

export default function AppSidebar() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [pendingRequests, setPendingRequests] = useState(0);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    getMyProfile()
      .then((data) => {
        if (!cancelled && data.avatarUrl) {
          setAvatarUrl(data.avatarUrl);
        }
      })
      .catch(() => {
        // Keep the sidebar usable even if the profile request fails.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const refresh = async () => {
      try {
        const requests = await getIncomingConnectionRequests();
        if (!cancelled) setPendingRequests(requests.length);
      } catch {
        // Ignore request badge refresh failures.
      }
    };

    const onLocalUpdate = () => refresh();
    window.addEventListener("connections:requests-updated", onLocalUpdate);

    refresh();

    const unsubscribeOnConnect = onWsConnect(() => {
      const subscription = safeSubscribe("/user/queue/notifications", () => {
        refresh();
      });

      return () => subscription?.unsubscribe();
    });

    return () => {
      cancelled = true;
      window.removeEventListener("connections:requests-updated", onLocalUpdate);
      unsubscribeOnConnect();
    };
  }, []);

  return (
    <aside className="sidebar">
      <div className="sidebar-top">
        <div className="brand">
          <img className="sidebarLogo" src="/logo.png" alt="Younggreenteco" />
          <div>
            <h1>Youngreenteco</h1>
            <span>Connect & Grow</span>
          </div>
        </div>

        <nav className="nav">
          <NavLink to="/dashboard" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <span className="nav-ico" aria-hidden="true">
              <FaHome />
            </span>
            <span className="nav-label">Dashboard</span>
          </NavLink>

          <NavLink to="/profile" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <span className="nav-ico" aria-hidden="true">
              <FaUser />
            </span>
            <span className="nav-label">My Profile</span>
          </NavLink>

          <div className="nav-section">Explore</div>

          <NavLink to="/projects" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <span className="nav-ico" aria-hidden="true">
              <FaProjectDiagram />
            </span>
            <span className="nav-label">Projects</span>
          </NavLink>

          <NavLink to="/publications" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <span className="nav-ico" aria-hidden="true">
              <FaBookOpen />
            </span>
            <span className="nav-label">Publications</span>
          </NavLink>

          <NavLink to="/announcements" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <span className="nav-ico" aria-hidden="true">
              <FaBullhorn />
            </span>
            <span className="nav-label">Announcements</span>
          </NavLink>

          <NavLink to="/messages" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <span className="nav-ico" aria-hidden="true">
              <FaComments />
            </span>
            <span className="nav-label">Messages</span>
          </NavLink>

          <NavLink to="/matching" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <span className="nav-ico" aria-hidden="true">
              <FaUsers />
            </span>
            <span className="nav-label">Matching</span>
          </NavLink>

          <NavLink to="/auto-matching" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <span className="nav-ico" aria-hidden="true">
              <FaMagic />
            </span>
            <span className="nav-label">Auto Matching</span>
          </NavLink>

          <NavLink
            to="/connections"
            end
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
          >
            <span className="nav-ico" aria-hidden="true">
              <FaUserFriends />
            </span>
            <span className="nav-label">Connections</span>
          </NavLink>

          <NavLink to="/connections/requests" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <span className="nav-ico" aria-hidden="true">
              <FaInbox />
            </span>
            <span className="nav-label">Requests</span>
            {pendingRequests > 0 ? <span className="badge">{pendingRequests}</span> : null}
          </NavLink>
        </nav>
      </div>

      <div className="sidebar-bottom">
        <button
          className="nav-item danger"
          type="button"
          onClick={() => {
            logout();
            navigate("/");
          }}
        >
          <span className="nav-ico" aria-hidden="true">
            <FaSignOutAlt />
          </span>
          <span className="nav-label">Logout</span>
        </button>

        <div className="sidebar-user">
          <div
            className="avatar"
            style={
              avatarUrl
                ? {
                    backgroundImage: `url(${avatarUrl})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }
                : undefined
            }
          />
          <div className="sidebar-user-meta">
            <strong>
              {user?.firstName} {user?.lastName}
            </strong>
            <span>{user?.role}</span>
          </div>
        </div>
      </div>
    </aside>
  );
}
